'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import LoadingImage from 'components/commercetools-ui/atoms/loading-image';
import Modal from 'components/commercetools-ui/organisms/modal';
import ModalPopUp from 'components/commercetools-ui/organisms/modal-pop-up';
import useImageSizes from 'helpers/hooks/useImageSizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { DEFAULT_PHOTO_HEIGHT, DEFAULT_PHOTO_WIDTH, MyPhotoItem, RemovePhotosResult } from 'helpers/hooks/useMyPhotos';
import { usePageLoader } from 'providers/loader';
import { interpolateCount, interpolatePhotoName } from '../../utils';
import WidgetPreview from '../widget-preview';

type WidgetModalProps = {
  heading?: string;
  albumTitle?: string;
  previewHeading?: string;
  previewPhotoAriaLabel?: string;
  previousPhotoAriaLabel?: string;
  nextPhotoAriaLabel?: string;
  previewPhotoErrorMessage?: string;
  removeBtnCtaLabel?: string;
  removePhotoSuccessMessage?: string;
  removePhotoErrorMessage?: string;
  goBackBtnCtaLabel?: string;
  cancelBtnCtaLabel?: string;
  confirmModalTitle?: string;
  confirmModalDescription?: string;
  hideBtnCtaLabel?: string;
  headingFontSize?: number;
  headingColor?: string;
  headingFontWeight?: 'light' | 'regular' | 'medium' | 'semibold' | 'bold';
  hideButtonBgColor?: string;
  hideButtonTextColor?: string;
  isOpen: boolean;
  photos: MyPhotoItem[];
  onHide: () => void;
  removePhoto: (photoId: string) => Promise<RemovePhotosResult>;
};

/**
 * Rendered height of one tile, in multiples of the column width: every tile is `w-full` with
 * `h-auto`, so its height is fixed by its own aspect ratio. The constant is the `gap-24` plus the
 * tile's `pb-24`, expressed in the same units at a typical column width — without it a column of
 * many short photos looks cheaper than it is.
 */
const TILE_SPACING_IN_COLUMN_WIDTHS = 0.25;

const estimateTileHeight = (photo: MyPhotoItem) => {
  const width = photo.width ?? DEFAULT_PHOTO_WIDTH;
  const height = photo.height ?? DEFAULT_PHOTO_HEIGHT;
  return height / width + TILE_SPACING_IN_COLUMN_WIDTHS;
};

/**
 * Distributes photos across `columnCount` masonry columns, preserving order.
 *
 * Round-robin (`index % columnCount`) balances the *count* per column, not the *height*, so a
 * column that happens to draw the portrait shots runs far taller than its neighbours and the grid
 * ends in a ragged row of blank space. Each photo goes to the currently shortest column instead,
 * using its known aspect ratio. The pass is sequential, so appending a later batch never moves a
 * photo that is already on screen.
 */
const buildColumns = (photos: MyPhotoItem[], columnCount: number) => {
  const columns: MyPhotoItem[][] = Array.from({ length: columnCount }, () => []);
  const columnHeights = new Array<number>(columnCount).fill(0);

  photos.forEach((photo) => {
    let shortestIndex = 0;
    for (let index = 1; index < columnCount; index += 1) {
      if (columnHeights[index] < columnHeights[shortestIndex]) shortestIndex = index;
    }

    columns[shortestIndex].push(photo);
    columnHeights[shortestIndex] += estimateTileHeight(photo);
  });

  return columns;
};

const INITIAL_VISIBLE_COUNT = 20;
const LOAD_MORE_BATCH = 8;

const WidgetModal = ({
  isOpen,
  photos,
  onHide,
  removePhoto,
  heading = '{count} Photos Selected',
  albumTitle,
  previewHeading = 'Preview',
  previewPhotoAriaLabel,
  previousPhotoAriaLabel,
  nextPhotoAriaLabel,
  previewPhotoErrorMessage,
  removeBtnCtaLabel = 'Remove',
  removePhotoSuccessMessage = 'Photo removed successfully.',
  removePhotoErrorMessage = 'Unable to remove photo. Please try again.',
  goBackBtnCtaLabel = 'Go Back',
  cancelBtnCtaLabel = 'Cancel',
  confirmModalTitle = 'Remove photo?',
  confirmModalDescription = 'This photo will be removed from your cart, saved projects and wishlist.',
  hideBtnCtaLabel = 'Hide',
  headingFontSize,
  headingColor,
  headingFontWeight,
  hideButtonBgColor,
  hideButtonTextColor,
}: WidgetModalProps) => {
  const fontWeightByStyle = {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  } as const;

  const [viewportWidth] = useMediaQuery();
  const gridImageSizes = useImageSizes({ sm: 0.5, md: 0.25, lg: 0.2, maxWidth: 210 });
  const { showLoader, hideLoader } = usePageLoader();

  const [previewPhotoId, setPreviewPhotoId] = useState<string | null>(null);
  const [removeTargetId, setRemoveTargetId] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [loadMoreSentinelNode, setLoadMoreSentinelNode] = useState<HTMLDivElement | null>(null);
  const [scrollContainerNode, setScrollContainerNode] = useState<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number | null>(null);
  const previousPhotosRef = useRef<MyPhotoItem[]>(photos);

  let columnCount = 2;
  if (viewportWidth >= 1240) {
    columnCount = 5;
  } else if (viewportWidth >= 905) {
    columnCount = 4;
  }

  const visiblePhotos = useMemo(() => photos.slice(0, visibleCount), [photos, visibleCount]);
  const photoColumns = useMemo(() => buildColumns(visiblePhotos, columnCount), [visiblePhotos, columnCount]);
  /* Columns are height-balanced, not round-robin, so a photo's grid number can only come from the source order. */
  const photoNumberById = useMemo(
    () => new Map(photos.map((photo, index) => [photo.id, index + 1])),
    [photos],
  );
  const isPreviewing = previewPhotoId !== null;
  const hasMorePhotos = visibleCount < photos.length;

  useEffect(() => {
    if (isOpen) return;
    setPreviewPhotoId(null);
    setRemoveTargetId(null);
    setIsRemoving(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const html = document.documentElement;
    const previousHtmlOverflow = html.style.overflow;
    html.style.overflow = 'hidden';

    return () => {
      html.style.overflow = previousHtmlOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    const previousPhotos = previousPhotosRef.current;
    previousPhotosRef.current = photos;

    if (!previewPhotoId || photos.some((photo) => photo.id === previewPhotoId)) return;

    if (photos.length === 0) {
      setPreviewPhotoId(null);
      return;
    }

    const removedIndex = previousPhotos.findIndex((photo) => photo.id === previewPhotoId);
    const fallbackIndex = Math.min(Math.max(removedIndex, 0), photos.length - 1);
    setPreviewPhotoId(photos[fallbackIndex].id);
  }, [photos, previewPhotoId]);

  useEffect(() => {
    setVisibleCount((prev) => {
      if (photos.length === 0) return 0;
      if (prev === 0) return Math.min(INITIAL_VISIBLE_COUNT, photos.length);
      return Math.min(prev, photos.length);
    });
  }, [photos.length]);

  useEffect(() => {
    if (!previewPhotoId) return;

    const previewIndex = photos.findIndex((photo) => photo.id === previewPhotoId);
    if (previewIndex < 0) return;

    setVisibleCount((prev) => Math.max(prev, Math.min(previewIndex + 1, photos.length)));
  }, [previewPhotoId, photos]);

  useEffect(() => {
    if (isPreviewing || scrollPositionRef.current === null || !scrollContainerNode) return;

    const targetScrollTop = scrollPositionRef.current;
    scrollPositionRef.current = null;

    requestAnimationFrame(() => {
      scrollContainerNode.scrollTop = targetScrollTop;
    });
  }, [isPreviewing, scrollContainerNode]);

  useEffect(() => {
    if (isPreviewing || !hasMorePhotos || !loadMoreSentinelNode || !scrollContainerNode) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setVisibleCount((prev) => Math.min(prev + LOAD_MORE_BATCH, photos.length));
        });
      },
      {
        root: scrollContainerNode,
        threshold: 0.5,
      },
    );

    observer.observe(loadMoreSentinelNode);
    return () => observer.disconnect();
  }, [isPreviewing, hasMorePhotos, loadMoreSentinelNode, scrollContainerNode, photos.length]);

  const confirmRemove = async () => {
    if (!removeTargetId || isRemoving) return;
    setIsRemoving(true);
    showLoader();
    try {
      const { success } = await removePhoto(removeTargetId);
      setRemoveTargetId(null);
      if (success) {
        toast.success(removePhotoSuccessMessage);
        return;
      }

      toast.error(removePhotoErrorMessage);
    } finally {
      setIsRemoving(false);
      hideLoader();
    }
  };

  const openPreview = (photoId: string) => {
    scrollPositionRef.current = scrollContainerNode?.scrollTop ?? 0;
    setPreviewPhotoId(photoId);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onHide}
        shouldCloseOnEsc
        shouldCloseOnOverlayClick
        preventScroll
        closeButton={false}
        overlayZIndex={1100}
        style={{
          content: { maxWidth: 'min(1040px, 92vw)', width: 'min(1040px, 92vw)' },
          overlay: { background: 'rgba(51, 51, 51, 0.6)' },
        }}
        data-ffid="mdl-my-photos-widget"
      >
        <div className="flex h-[86vh] flex-col" data-ffid="ctr-my-photos-widget-modal">
          <h2
            className="p-16 text-center"
            style={{
              color: headingColor,
              fontSize: headingFontSize ? `${headingFontSize}px` : undefined,
              fontWeight: headingFontWeight ? fontWeightByStyle[headingFontWeight] : undefined,
            }}
            data-ffid="lbl-my-photos-widget-modal-title"
          >
            {isPreviewing ? previewHeading : (albumTitle ?? interpolateCount(heading, photos.length))}
          </h2>

          <div
            ref={setScrollContainerNode}
            className={`flex-1 px-40 ${isPreviewing ? 'overflow-hidden' : 'overflow-y-auto'}`}
          >
            {isPreviewing ? (
              <div className="flex size-full flex-col items-center">
                <WidgetPreview
                  photos={photos}
                  activePhotoId={previewPhotoId as string}
                  onSelectPhoto={setPreviewPhotoId}
                  onGoBack={() => setPreviewPhotoId(null)}
                  onRemove={setRemoveTargetId}
                  goBackCtaLabel={goBackBtnCtaLabel}
                  removeCtaLabel={removeBtnCtaLabel}
                  previewErrorMessage={previewPhotoErrorMessage}
                  previousPhotoLabel={previousPhotoAriaLabel}
                  nextPhotoLabel={nextPhotoAriaLabel}
                />
              </div>
            ) : (
              <>
                <div className="flex gap-12">
                  {photoColumns.map((column, columnIndex) => (
                    <div key={columnIndex} className="flex flex-1 flex-col gap-24">
                      {column.map((photo) => {
                        const photoNumber = photoNumberById.get(photo.id);

                        return (
                          <div key={photo.id} className="border-b border-neutral-200 pb-24">
                            <button
                              type="button"
                              onClick={() => openPreview(photo.id)}
                              className="block w-full overflow-hidden rounded-lg bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#018463]"
                              aria-label={interpolatePhotoName(
                                previewPhotoAriaLabel ?? 'Preview {photoName}',
                                photo.alt,
                              )}
                              data-ffid={`btn-my-photos-widget-photo-${photoNumber}`}
                            >
                              <LoadingImage
                                src={photo.gridSrc}
                                alt={photo.alt}
                                width={photo.width ?? DEFAULT_PHOTO_WIDTH}
                                height={photo.height ?? DEFAULT_PHOTO_HEIGHT}
                                sizes={gridImageSizes}
                                wrapperClassName="w-full"
                                className="h-auto w-full object-cover"
                                data-ffid={`img-my-photos-widget-grid-${photoNumber}`}
                              />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {hasMorePhotos && (
                  <div
                    ref={setLoadMoreSentinelNode}
                    className="h-1 w-full"
                    data-ffid="ctr-my-photos-widget-modal-load-more-sentinel"
                  />
                )}
              </>
            )}
          </div>

          {!isPreviewing && (
            <div className="flex justify-center border-t border-neutral-200 px-24 py-20">
              <button
                type="button"
                onClick={onHide}
                className="inline-flex h-44 min-w-160 items-center justify-center rounded-full bg-[#018463] px-32 text-14 font-medium text-white transition hover:bg-[#349D82] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#349D82] focus-visible:ring-offset-2"
                style={{ backgroundColor: hideButtonBgColor, color: hideButtonTextColor }}
                data-ffid="btn-my-photos-widget-hide"
              >
                {hideBtnCtaLabel}
              </button>
            </div>
          )}
        </div>
      </Modal>

      <ModalPopUp
        isOpen={Boolean(removeTargetId)}
        onClose={() => setRemoveTargetId(null)}
        title={confirmModalTitle}
        messageDescription={confirmModalDescription}
        overlayZIndex={1200}
        ffidValue="my-photos-widget-remove"
        showCancelButton
        cancelText={cancelBtnCtaLabel}
        onCancel={() => setRemoveTargetId(null)}
        actionType="remove"
        actionText={removeBtnCtaLabel}
        onAction={confirmRemove}
        isActionDisabled={isRemoving}
      />
    </>
  );
};

export default WidgetModal;
